<template>
  <div class="flex h-screen bg-[#F8FAFC] font-sans text-slate-900 overflow-hidden">
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&family=Roboto:wght@400;500;700;900&family=Montserrat:wght@400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet">

    <aside class="w-72 bg-white border-r border-slate-200 flex flex-col z-20 shadow-sm">
      <div class="p-6 border-b border-slate-100">
        <h1 class="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Kiosk Canvas</h1>
        <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Mode Position Absolue</p>
      </div>

      <LanguageTabs
        v-model="activeLanguage"
        :languages="availableLanguages"
        :configured-languages="configuredLanguages"
        @copy-from="copyLanguageConfig"
      />

      <div class="flex-1 overflow-y-auto p-4 space-y-6">
        <!-- Section paramètres Canvas -->
        <div class="pb-4 border-b border-slate-100">
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">Paramètres Canvas</h3>
          <div class="space-y-3 px-2">
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Type de fond</label>
              <select v-model="canvasBackgroundType" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="color">Couleur unie</option>
                <option value="gradient">Dégradé</option>
                <option value="image">Image</option>
              </select>
            </div>
            
            <div v-if="canvasBackgroundType === 'color'">
              <label class="text-[10px] text-slate-500 mb-1 block">Couleur de fond</label>
              <input type="color" v-model="currentLanguageConfig.branding.colors.background" class="w-full h-10 rounded cursor-pointer" />
            </div>

            <div v-if="canvasBackgroundType === 'gradient'" class="space-y-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Couleur 1</label>
                <input type="color" v-model="gradientColor1" class="w-full h-8 rounded cursor-pointer" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Couleur 2</label>
                <input type="color" v-model="gradientColor2" class="w-full h-8 rounded cursor-pointer" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Direction</label>
                <select v-model="gradientDirection" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="to right">Horizontal →</option>
                  <option value="to bottom">Vertical ↓</option>
                  <option value="to bottom right">Diagonal ↘</option>
                  <option value="135deg">Diagonal ↗</option>
                </select>
              </div>
            </div>

            <div v-if="canvasBackgroundType === 'image'">
              <label class="text-[10px] text-slate-500 mb-1 block">URL de l'image</label>
              <MediaPicker
                v-model="canvasBackgroundImage"
                placeholder="URL de l'image de fond"
                button-text="Choisir l'image de fond"
                :agency-id="agencyId"
              />
              <label class="text-[10px] text-slate-500 mb-1 block mt-2">Ajustement</label>
              <select v-model="canvasBackgroundSize" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="cover">Couvrir</option>
                <option value="contain">Contenir</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-4 px-2">Éléments sur la scène</h3>
          <div class="space-y-1">
            <template v-for="(container, cIdx) in structuredComponents" :key="cIdx">
              <div v-for="(block, bIdx) in container.blocks" :key="bIdx"
                   @click="selectBlock(cIdx, bIdx)"
                   :class="['group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all border border-transparent', 
                            selectedBlockIndex === bIdx && selectedContainerIndex === cIdx ? 'bg-blue-50 border-blue-100 text-blue-700' : 'hover:bg-slate-50 text-slate-600']">
                <div class="flex items-center gap-3">
                  <span class="text-lg">{{ getBlockIcon(block.type) }}</span>
                  <span class="text-sm font-semibold capitalize">{{ getBlockLabel(block.type) }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
        
        <button @click="openAddBlockMenu(0)" class="w-full py-3 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 text-xs font-bold hover:border-blue-400 hover:text-blue-500 transition-all uppercase">
          + Ajouter un bloc
        </button>
      </div>

      <div class="p-4 border-t border-slate-100 space-y-2">
        <!-- Boutons Import/Export -->
        <div class="flex gap-2">
          <button @click="exportConfig"
                  class="flex-1 py-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all">
            📥 Export
          </button>
          <label class="flex-1 py-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all cursor-pointer text-center">
            📤 Import
            <input type="file" accept=".json" @change="importConfig" class="hidden" />
          </label>
        </div>

        <button @click="saveConfig" class="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold shadow-xl hover:bg-blue-600 transition-all flex items-center justify-center gap-2">
          <span>🚀 Publier sur la borne</span>
        </button>
      </div>
    </aside>

    <main class="flex-1 relative bg-slate-100 overflow-hidden p-12 flex justify-center items-center">
      <div class="relative bg-slate-800 p-4 rounded-[4rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.4)] border-[8px] border-slate-700">
        
        <div 
          id="kiosk-canvas"
          class="relative overflow-hidden bg-white w-[375px] h-[667px] rounded-[2rem] shadow-inner"
          :style="getCanvasBackgroundStyle()"
        >
          <template v-for="(container, cIdx) in structuredComponents" :key="'c-'+cIdx">
            <div 
              v-for="(block, bIdx) in container.blocks" 
              :key="'b-'+bIdx"
              class="absolute group cursor-move select-none"
              :class="{ 'ring-2 ring-blue-500 z-50': selectedBlockIndex === bIdx && selectedContainerIndex === cIdx }"
              :style="{
                left: (block.x / SCALE_FACTOR) + 'px',
                top: (block.y / SCALE_FACTOR) + 'px',
                width: (block.width / SCALE_FACTOR) + 'px',
                height: (block.height / SCALE_FACTOR) + 'px',
              }"
              @mousedown.stop="startDrag($event, cIdx, bIdx)"
            >
              <div class="w-full h-full overflow-hidden pointer-events-none" 
                   :style="{ padding: (block.padding / SCALE_FACTOR) + 'px' }">
                
                <!-- Text Block -->
                <div v-if="block.type === 'text'" :style="getTextStyle(block)">
                  {{ block.text }}
                </div>
                
                <!-- Image Block -->
                <img v-else-if="block.type === 'image'" 
                     :src="block.icon || block.src" 
                     :style="{ 
                       objectFit: block.fit || 'cover',
                       borderRadius: (block.borderRadius / SCALE_FACTOR) + 'px',
                       opacity: block.opacity !== undefined ? block.opacity : 1
                     }" 
                     class="w-full h-full" />
                
                <!-- Button / Print Ticket -->
                <div v-else-if="['button', 'print_ticket'].includes(block.type)" 
                     class="w-full h-full flex items-center justify-center"
                     :style="{
                        backgroundColor: block.backgroundColor, 
                        color: block.color, 
                        borderRadius: (block.borderRadius / SCALE_FACTOR) + 'px',
                        fontSize: (block.fontSize / SCALE_FACTOR) + 'px',
                        fontWeight: block.fontWeight,
                        textAlign: block.textAlign,
                        fontFamily: block.fontFamily || 'Plus Jakarta Sans'
                     }">
                  {{ block.text }}
                </div>

                <!-- Video Block -->
                <div v-else-if="block.type === 'video'" class="w-full h-full bg-black rounded-lg flex items-center justify-center text-white text-[10px]">
                  🎬 Vidéo iFrame
                </div>

                <!-- Shape Blocks -->
                <div v-else-if="['rectangle', 'circle', 'triangle'].includes(block.type)"
                     class="w-full h-full"
                     :style="getShapeStyle(block)">
                </div>
              </div>

              <div class="absolute -top-6 left-0 bg-blue-600 text-white text-[8px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                X: {{ Math.round(block.x) }} Y: {{ Math.round(block.y) }}
              </div>

              <button v-if="selectedBlockIndex === bIdx && selectedContainerIndex === cIdx" 
                      @click.stop="deleteBlock(cIdx, bIdx)" 
                      class="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs shadow-lg pointer-events-auto">×</button>
            </div>
          </template>

          <!-- Guides d'alignement -->
          <div v-for="(guide, idx) in alignmentGuides.vertical" :key="'v-'+idx"
               class="absolute top-0 bottom-0 w-[1px] pointer-events-none z-50"
               :class="guide.type === 'canvas' ? 'bg-purple-400' : 'bg-blue-400'"
               :style="{ left: (guide.value / SCALE_FACTOR) + 'px' }">
            <div class="absolute top-1/2 -translate-y-1/2 -left-3 w-6 h-6 rounded-full flex items-center justify-center text-[8px] text-white"
                 :class="guide.type === 'canvas' ? 'bg-purple-400' : 'bg-blue-400'">
              ⬍⬍
            </div>
          </div>

          <div v-for="(guide, idx) in alignmentGuides.horizontal" :key="'h-'+idx"
               class="absolute left-0 right-0 h-[1px] pointer-events-none z-50"
               :class="guide.type === 'canvas' ? 'bg-purple-400' : 'bg-blue-400'"
               :style="{ top: (guide.value / SCALE_FACTOR) + 'px' }">
            <div class="absolute left-1/2 -translate-x-1/2 -top-3 w-6 h-6 rounded-full flex items-center justify-center text-[8px] text-white"
                 :class="guide.type === 'canvas' ? 'bg-purple-400' : 'bg-blue-400'">
              ⬍⬍
            </div>
          </div>
        </div>
      </div>
    </main>

    <aside class="w-80 bg-white border-l border-slate-200 flex flex-col z-20 shadow-xl overflow-hidden">
      <div v-if="activeBlock" class="flex flex-col h-full overflow-hidden">
        <div class="p-6 border-b border-slate-100 bg-blue-50/50 flex justify-between items-center">
            <h2 class="font-bold text-slate-800 uppercase text-xs">Propriétés du Bloc</h2>
            <button @click="clearSelection" class="text-slate-400">✕</button>
        </div>

        <div class="flex-1 overflow-y-auto p-6 space-y-6">
          <!-- Position & Taille -->
          <div class="space-y-4">
            <label class="text-[10px] font-black text-slate-400 uppercase">Position & Taille (Pixels Borne)</label>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-[9px] text-slate-400 uppercase">Axe X</label>
                <input type="number" v-model.number="activeBlock.x" class="w-full p-2 bg-slate-50 border rounded-lg text-sm" />
              </div>
              <div>
                <label class="text-[9px] text-slate-400 uppercase">Axe Y</label>
                <input type="number" v-model.number="activeBlock.y" class="w-full p-2 bg-slate-50 border rounded-lg text-sm" />
              </div>
              <div>
                <label class="text-[9px] text-slate-400 uppercase">Largeur</label>
                <input type="number" v-model.number="activeBlock.width" class="w-full p-2 bg-slate-50 border rounded-lg text-sm" />
              </div>
              <div>
                <label class="text-[9px] text-slate-400 uppercase">Hauteur</label>
                <input type="number" v-model.number="activeBlock.height" class="w-full p-2 bg-slate-50 border rounded-lg text-sm" />
              </div>
            </div>
          </div>

          <!-- Border Radius (pour tous les types sauf triangle) -->
          <div v-if="activeBlock.type !== 'triangle'" class="space-y-2 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Coins arrondis</label>
            <input type="range" v-model.number="activeBlock.borderRadius" min="0" max="200" class="w-full" />
            <div class="text-xs text-slate-500 text-center">{{ activeBlock.borderRadius }}px</div>
          </div>

          <!-- Texte & Style (pour text, button, print_ticket) -->
          <div v-if="['text', 'button', 'print_ticket'].includes(activeBlock.type)" class="space-y-4 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Texte & Style</label>
            <textarea v-model="activeBlock.text" rows="2" class="w-full p-3 bg-slate-50 rounded-xl border border-slate-100 text-sm focus:ring-2 focus:ring-blue-500 outline-none"></textarea>
            
            <div>
              <label class="text-[10px] text-slate-400">Police</label>
              <select v-model="activeBlock.fontFamily" class="w-full p-2 bg-slate-50 rounded-lg border text-sm">
                <option value="Plus Jakarta Sans">Plus Jakarta Sans</option>
                <option value="Inter">Inter</option>
                <option value="Poppins">Poppins</option>
                <option value="Roboto">Roboto</option>
                <option value="Montserrat">Montserrat</option>
                <option value="Playfair Display">Playfair Display</option>
                <option value="Space Mono">Space Mono</option>
              </select>
            </div>

            <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-[10px] text-slate-400">Taille Police</label>
                  <input type="number" v-model.number="activeBlock.fontSize" class="w-full p-2 bg-slate-50 rounded-lg border text-sm" />
                </div>
                <div>
                  <label class="text-[10px] text-slate-400">Graisse</label>
                  <select v-model="activeBlock.fontWeight" class="w-full p-2 bg-slate-50 rounded-lg border text-sm">
                      <option value="400">Normal</option>
                      <option value="500">Medium</option>
                      <option value="600">Semi-Bold</option>
                      <option value="700">Bold</option>
                      <option value="800">Extra-Bold</option>
                  </select>
                </div>
            </div>

            <div>
              <label class="text-[10px] text-slate-400">Alignement</label>
              <div class="flex gap-2">
                <button @click="activeBlock.textAlign = 'left'" 
                        :class="['flex-1 p-2 rounded-lg border text-xs', activeBlock.textAlign === 'left' ? 'bg-blue-500 text-white' : 'bg-slate-50']">
                  Gauche
                </button>
                <button @click="activeBlock.textAlign = 'center'" 
                        :class="['flex-1 p-2 rounded-lg border text-xs', activeBlock.textAlign === 'center' ? 'bg-blue-500 text-white' : 'bg-slate-50']">
                  Centre
                </button>
                <button @click="activeBlock.textAlign = 'right'" 
                        :class="['flex-1 p-2 rounded-lg border text-xs', activeBlock.textAlign === 'right' ? 'bg-blue-500 text-white' : 'bg-slate-50']">
                  Droite
                </button>
              </div>
            </div>

            <div class="flex items-center gap-4">
              <div class="flex-1">
                <label class="text-[10px] text-slate-400">Couleur texte</label>
                <input type="color" v-model="activeBlock.color" class="w-full h-8 rounded cursor-pointer" />
              </div>
              <div v-if="activeBlock.type !== 'text'" class="flex-1">
                <label class="text-[10px] text-slate-400">Fond</label>
                <input type="color" v-model="activeBlock.backgroundColor" class="w-full h-8 rounded cursor-pointer" />
              </div>
            </div>
          </div>

          <!-- Image customization -->
          <div v-if="activeBlock.type === 'image'" class="space-y-4 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Image</label>
            
            <div>
              <label class="text-[10px] text-slate-400">URL de l'image</label>
              <MediaPicker
                v-model="activeBlock.icon"
                placeholder="URL de l'icône"
                button-text="Choisir une icône"
                :agency-id="agencyId"
              />
            </div>

            <div>
              <label class="text-[10px] text-slate-400">Ajustement (Object-fit)</label>
              <select v-model="activeBlock.fit" class="w-full p-2 bg-slate-50 rounded-lg border text-sm">
                <option value="cover">Cover (Couvrir)</option>
                <option value="contain">Contain (Contenir)</option>
                <option value="fill">Fill (Remplir)</option>
                <option value="none">None (Aucun)</option>
                <option value="scale-down">Scale-down (Réduire)</option>
              </select>
            </div>

            <div>
              <label class="text-[10px] text-slate-400">Opacité</label>
              <input type="range" v-model.number="activeBlock.opacity" min="0" max="1" step="0.1" class="w-full" />
              <div class="text-xs text-slate-500 text-center">{{ Math.round(activeBlock.opacity * 100) }}%</div>
            </div>
          </div>

          <!-- Shape customization -->
          <div v-if="['rectangle', 'circle', 'triangle'].includes(activeBlock.type)" class="space-y-4 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Forme</label>
            
            <div>
              <label class="text-[10px] text-slate-400">Couleur de remplissage</label>
              <input type="color" v-model="activeBlock.backgroundColor" class="w-full h-10 rounded cursor-pointer" />
            </div>

            <div>
              <label class="text-[10px] text-slate-400">Bordure</label>
              <div class="flex gap-2">
                <input type="number" v-model.number="activeBlock.borderWidth" placeholder="Épaisseur" class="flex-1 p-2 bg-slate-50 rounded-lg border text-sm" min="0" />
                <input type="color" v-model="activeBlock.borderColor" class="w-16 h-10 rounded cursor-pointer" />
              </div>
            </div>

            <div>
              <label class="text-[10px] text-slate-400">Opacité</label>
              <input type="range" v-model.number="activeBlock.opacity" min="0" max="1" step="0.1" class="w-full" />
              <div class="text-xs text-slate-500 text-center">{{ Math.round(activeBlock.opacity * 100) }}%</div>
            </div>
          </div>

          <!-- Action pour boutons -->
          <div v-if="['button', 'print_ticket'].includes(activeBlock.type)" class="space-y-4 pt-4 border-t">
            <label class="text-[10px] font-black text-slate-400 uppercase">Action au clic</label>
            <select v-model="activeBlock.action.type" class="w-full p-3 bg-slate-50 rounded-xl border text-sm">
                <option value="none">Aucune</option>
                <option value="queue">File d'attente</option>
                <option value="url">URL</option>
            </select>

            <div v-if="activeBlock.action.type === 'queue'">
              <label class="text-[10px] text-slate-400 mb-1 block">Choisir la file</label>
              <select v-model="activeBlock.action.queueId" class="w-full p-2 bg-slate-50 rounded-lg border text-sm">
                <option :value="null">Sélectionner...</option>
                <option v-for="queue in queues" :key="queue._id" :value="queue._id">
                  {{ queue.prefix }} - {{ queue.name }}
                </option>
              </select>
            </div>

            <input v-if="activeBlock.action.type === 'url'"
                   type="text"
                   v-model="activeBlock.action.url"
                   placeholder="https://..."
                   class="w-full p-2 bg-slate-50 rounded-lg border text-sm" />
          </div>

          <button @click="deleteBlock(selectedContainerIndex, selectedBlockIndex)" class="w-full py-3 text-red-500 bg-red-50 rounded-xl text-xs font-bold mt-4 hover:bg-red-100 transition-colors">Supprimer l'élément</button>
        </div>
      </div>

      <div v-else class="flex-1 flex flex-col items-center justify-center p-12 text-center text-slate-400">
          <div class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-2xl mb-4 grayscale">🎯</div>
          <p class="text-xs font-semibold">Cliquez sur un élément du canvas pour le régler.</p>
      </div>
    </aside>

    <Transition name="fade">
        <div v-if="showAddBlockMenu" class="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-6">
            <div class="bg-white rounded-[2.5rem] p-10 max-w-2xl w-full shadow-2xl scale-up max-h-[80vh] overflow-y-auto">
                <div class="flex justify-between items-center mb-8">
                    <h2 class="text-2xl font-extrabold text-slate-900">Nouveau Composant</h2>
                    <button @click="showAddBlockMenu = false" class="text-2xl text-slate-300">✕</button>
                </div>
                <div class="grid grid-cols-3 gap-6">
                    <button v-for="t in blockTypes" :key="t.id" @click="addBlockToContainer(t.id)" 
                            class="flex flex-col items-center gap-4 p-8 rounded-[2rem] border-2 border-slate-50 hover:border-indigo-500 hover:bg-indigo-50 transition-all">
                        <span class="text-5xl">{{ t.icon }}</span>
                        <span class="font-bold text-slate-700 text-sm text-center">{{ t.label }}</span>
                    </button>
                </div>
            </div>
        </div>
    </Transition>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { toast } from "vue3-toastify";
import { useRoute } from 'vue-router';
import LanguageTabs from '@/components/LanguageTabs.vue';
import MediaPicker from '@/components/MediaPicker.vue';

// ----------------- CONFIGURATION DU CANVAS -----------------
const TARGET_WIDTH = 1080;
const EDITOR_WIDTH = 375; 
const SCALE_FACTOR = TARGET_WIDTH / EDITOR_WIDTH; 

// Liste complète des types de blocs incluant les formes
const blockTypes = [
    { id: 'text', label: 'Texte', icon: '✍️' },
    { id: 'image', label: 'Image', icon: '🖼️' },
    { id: 'print_ticket', label: 'Bouton Ticket', icon: '🎟️' },
    { id: 'button', label: 'Bouton URL', icon: '🔘' },
    { id: 'video', label: 'Vidéo iFrame', icon: '🎬' },
    { id: 'rectangle', label: 'Rectangle', icon: '▭' },
    { id: 'circle', label: 'Cercle', icon: '●' },
    { id: 'triangle', label: 'Triangle', icon: '▲' }
];

const route = useRoute();
const agencyId = route.params.kioskId;

// États réactifs pour les langues
const activeLanguage = ref('fr');
// ✅ Langues dynamiques récupérées depuis l'API
const availableLanguages = ref([
  { code: 'fr', label: 'Français', flag: '🇫🇷' } // Valeur par défaut
]);

// Files d'attente
const queues = ref([]);

// États réactifs
const selectedContainerIndex = ref(null);
const selectedBlockIndex = ref(null);
const showAddBlockMenu = ref(false);
const activeContainerForAddingBlock = ref(null);

// États pour le background du canvas
const canvasBackgroundType = ref('color');
const gradientColor1 = ref('#667eea');
const gradientColor2 = ref('#764ba2');
const gradientDirection = ref('to right');
const canvasBackgroundImage = ref('');
const canvasBackgroundSize = ref('cover');

const config = ref({
  agencyId: agencyId,
  languages: {
    fr: {
      branding: { colors: { background: '#F8FAFC' }, canvasBackground: { type: 'color' } },
      layout: { screenPadding: 24 },
      containers: []
    }
  }
});

// Computed pour les langues configurées
const configuredLanguages = computed(() => {
  return Object.keys(config.value.languages || {});
});

// Computed pour la configuration de la langue active
const currentLanguageConfig = computed(() => {
  return config.value.languages[activeLanguage.value] || null;
});

const ensureLanguageExists = (lang) => {
  if (!config.value.languages[lang]) {
    config.value.languages[lang] = {
      branding: { 
        colors: { background: '#F8FAFC' }, 
        canvasBackground: { type: 'color' } 
      },
      layout: { screenPadding: 24 },
      containers: [{ type: 'main_canvas', blocks: [] }]
    };
  }
};

watch(activeLanguage, (newLang) => {
  ensureLanguageExists(newLang);
}, { immediate: true });

// Computed pour les composants de la langue active
const structuredComponents = computed({
  get: () => currentLanguageConfig.value.containers || [],
  set: (val) => { currentLanguageConfig.value.containers = val; }
});

// --- HELPERS ---
const getBlockIcon = (type) => {
    return blockTypes.find(t => t.id === type)?.icon || '📦';
};

const getBlockLabel = (type) => {
    return blockTypes.find(t => t.id === type)?.label || type;
};

// --- COPIE DE CONFIG ENTRE LANGUES ---
const copyLanguageConfig = ({ from, to }) => {
  if (config.value.languages[from]) {
    config.value.languages[to] = JSON.parse(JSON.stringify(config.value.languages[from]));
    toast.success(`Configuration copiée de ${from} vers ${to}`);
  }
};

// --- STYLE DU CANVAS ---
const getCanvasBackgroundStyle = () => {
    if (canvasBackgroundType.value === 'gradient') {
        return {
            background: `linear-gradient(${gradientDirection.value}, ${gradientColor1.value}, ${gradientColor2.value})`
        };
    } else if (canvasBackgroundType.value === 'image' && canvasBackgroundImage.value) {
        return {
            backgroundImage: `url(${canvasBackgroundImage.value})`,
            backgroundSize: canvasBackgroundSize.value,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        };
    }
    return {
        backgroundColor: currentLanguageConfig.value.branding.colors.background
    };
};

// --- LOGIQUE DE DÉPLACEMENT (DRAG & DROP) AVEC GUIDES D'ALIGNEMENT ---
const isDragging = ref(false);
const startMousePos = { x: 0, y: 0 };
const startBlockPos = { x: 0, y: 0 };
const alignmentGuides = ref({ vertical: [], horizontal: [] });

const startDrag = (e, cIdx, bIdx) => {
    selectedContainerIndex.value = cIdx;
    selectedBlockIndex.value = bIdx;
    isDragging.value = true;

    const block = structuredComponents.value[cIdx].blocks[bIdx];
    startMousePos.x = e.clientX;
    startMousePos.y = e.clientY;
    startBlockPos.x = block.x || 0;
    startBlockPos.y = block.y || 0;

    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('mouseup', stopDrag);
};

const handleDrag = (e) => {
    if (!isDragging.value) return;
    const block = structuredComponents.value[selectedContainerIndex.value].blocks[selectedBlockIndex.value];
    const dx = e.clientX - startMousePos.x;
    const dy = e.clientY - startMousePos.y;

    const newX = startBlockPos.x + (dx * SCALE_FACTOR);
    const newY = startBlockPos.y + (dy * SCALE_FACTOR);

    const guides = calculateAlignmentGuides(
        selectedContainerIndex.value,
        selectedBlockIndex.value,
        newX,
        newY,
        block.width,
        block.height
    );

    const snapTolerance = 10;
    let snappedX = newX;
    let snappedY = newY;

    if (guides.vertical.length > 0) {
        const closestV = guides.vertical[0];
        if (Math.abs(closestV.value - (newX + block.width / 2)) < snapTolerance) {
            snappedX = closestV.value - block.width / 2;
        } else if (Math.abs(closestV.value - newX) < snapTolerance) {
            snappedX = closestV.value;
        } else if (Math.abs(closestV.value - (newX + block.width)) < snapTolerance) {
            snappedX = closestV.value - block.width;
        }
    }

    if (guides.horizontal.length > 0) {
        const closestH = guides.horizontal[0];
        if (Math.abs(closestH.value - (newY + block.height / 2)) < snapTolerance) {
            snappedY = closestH.value - block.height / 2;
        } else if (Math.abs(closestH.value - newY) < snapTolerance) {
            snappedY = closestH.value;
        } else if (Math.abs(closestH.value - (newY + block.height)) < snapTolerance) {
            snappedY = closestH.value - block.height;
        }
    }

    block.x = snappedX;
    block.y = snappedY;
    alignmentGuides.value = guides;
};

const stopDrag = () => {
    isDragging.value = false;
    alignmentGuides.value = { vertical: [], horizontal: [] };
    window.removeEventListener('mousemove', handleDrag);
    window.removeEventListener('mouseup', stopDrag);
};

const calculateAlignmentGuides = (currentCIdx, currentBIdx, x, y, width, height) => {
    const guides = { vertical: [], horizontal: [] };
    const canvasWidth = 1080;
    const canvasHeight = 1920;
    
    const left = x;
    const right = x + width;
    const centerX = x + width / 2;
    const top = y;
    const bottom = y + height;
    const centerY = y + height / 2;

    guides.vertical.push({ value: canvasWidth / 2, type: 'canvas' });
    guides.horizontal.push({ value: canvasHeight / 2, type: 'canvas' });

    structuredComponents.value.forEach((container, cIdx) => {
        container.blocks.forEach((block, bIdx) => {
            if (cIdx === currentCIdx && bIdx === currentBIdx) return;

            const bLeft = block.x;
            const bRight = block.x + block.width;
            const bCenterX = block.x + block.width / 2;
            const bTop = block.y;
            const bBottom = block.y + block.height;
            const bCenterY = block.y + block.height / 2;

            [
                { value: bLeft, align: 'left' },
                { value: bRight, align: 'right' },
                { value: bCenterX, align: 'center' }
            ].forEach(g => {
                if (Math.abs(g.value - left) < 20 || 
                    Math.abs(g.value - right) < 20 || 
                    Math.abs(g.value - centerX) < 20) {
                    guides.vertical.push({ value: g.value, type: 'block', align: g.align });
                }
            });

            [
                { value: bTop, align: 'top' },
                { value: bBottom, align: 'bottom' },
                { value: bCenterY, align: 'center' }
            ].forEach(g => {
                if (Math.abs(g.value - top) < 20 || 
                    Math.abs(g.value - bottom) < 20 || 
                    Math.abs(g.value - centerY) < 20) {
                    guides.horizontal.push({ value: g.value, type: 'block', align: g.align });
                }
            });
        });
    });

    return guides;
};

// --- GESTION DES SÉLECTIONS ET BLOCS ---
const selectBlock = (cIdx, bIdx) => { 
    selectedContainerIndex.value = cIdx; 
    selectedBlockIndex.value = bIdx; 
};

const clearSelection = () => {
    selectedContainerIndex.value = null;
    selectedBlockIndex.value = null;
};

const openAddBlockMenu = (cIdx) => {
    activeContainerForAddingBlock.value = cIdx;
    showAddBlockMenu.value = true;
};

const addBlockToContainer = (type) => {
    if (selectedContainerIndex.value === null && structuredComponents.value.length === 0) {
        structuredComponents.value.push({ type: 'main_canvas', blocks: [] });
    }
    
    const targetIdx = activeContainerForAddingBlock.value || 0;
    
    const baseBlock = {
        id: "id_" + Math.random().toString(36).substring(2, 9),
        type,
        x: 50, 
        y: 50,
        width: 400,
        height: 150,
        borderRadius: 20,
        padding: 20,
        opacity: 1
    };

    let newBlock;

    switch(type) {
        case 'text':
            newBlock = {
                ...baseBlock,
                text: 'Nouveau texte',
                fontSize: 40,
                fontWeight: '600',
                fontFamily: 'Plus Jakarta Sans',
                textAlign: 'center',
                color: '#1E293B',
                backgroundColor: 'transparent'
            };
            break;

        case 'image':
            newBlock = {
                ...baseBlock,
                icon: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809',
                src: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809',
                fit: 'cover',
                borderRadius: 20
            };
            break;

        case 'button':
        case 'print_ticket':
            newBlock = {
                ...baseBlock,
                text: type === 'print_ticket' ? 'Prendre un ticket' : 'Bouton',
                fontSize: 40,
                fontWeight: '600',
                fontFamily: 'Plus Jakarta Sans',
                textAlign: 'center',
                backgroundColor: '#4F46E5',
                color: '#FFFFFF',
                action: { type: 'none', queueId: '', url: '' }
            };
            break;

        case 'video':
            newBlock = {
                ...baseBlock,
                videoUrl: '',
                borderRadius: 20
            };
            break;

        case 'rectangle':
            newBlock = {
                ...baseBlock,
                width: 300,
                height: 200,
                backgroundColor: '#4F46E5',
                borderWidth: 0,
                borderColor: '#000000',
                borderRadius: 20
            };
            break;

        case 'circle':
            newBlock = {
                ...baseBlock,
                width: 200,
                height: 200,
                backgroundColor: '#EC4899',
                borderWidth: 0,
                borderColor: '#000000',
                borderRadius: 9999
            };
            break;

        case 'triangle':
            newBlock = {
                ...baseBlock,
                width: 200,
                height: 200,
                backgroundColor: '#10B981',
                borderWidth: 0,
                borderColor: '#000000'
            };
            break;

        default:
            newBlock = baseBlock;
    }

    structuredComponents.value[targetIdx].blocks.push(newBlock);
    showAddBlockMenu.value = false;
};

const deleteBlock = (cIdx, bIdx) => {
    structuredComponents.value[cIdx].blocks.splice(bIdx, 1);
    clearSelection();
};

// --- STYLES ---
const activeBlock = computed(() => {
    if (selectedContainerIndex.value !== null && selectedBlockIndex.value !== null) {
        return structuredComponents.value[selectedContainerIndex.value].blocks[selectedBlockIndex.value];
    }
    return null;
});

const getTextStyle = (block) => {
    return {
        color: block.color || '#1E293B',
        fontSize: (block.fontSize / SCALE_FACTOR) + 'px',
        fontWeight: block.fontWeight || '400',
        textAlign: block.textAlign || 'center',
        fontFamily: block.fontFamily || 'Plus Jakarta Sans',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: block.textAlign === 'left' ? 'flex-start' : block.textAlign === 'right' ? 'flex-end' : 'center'
    };
};

const getShapeStyle = (block) => {
    const style = {
        backgroundColor: block.backgroundColor || '#4F46E5',
        opacity: block.opacity !== undefined ? block.opacity : 1
    };

    if (block.borderWidth && block.borderWidth > 0) {
        style.border = `${block.borderWidth / SCALE_FACTOR}px solid ${block.borderColor || '#000000'}`;
    }

    if (block.type === 'circle') {
        style.borderRadius = '50%';
    } else if (block.type === 'rectangle') {
        style.borderRadius = (block.borderRadius / SCALE_FACTOR) + 'px';
    } else if (block.type === 'triangle') {
        // CSS Triangle
        style.width = '0';
        style.height = '0';
        style.backgroundColor = 'transparent';
        style.borderLeft = `${block.width / SCALE_FACTOR / 2}px solid transparent`;
        style.borderRight = `${block.width / SCALE_FACTOR / 2}px solid transparent`;
        style.borderBottom = `${block.height / SCALE_FACTOR}px solid ${block.backgroundColor || '#10B981'}`;
    }

    return style;
};

// --- API ---
const fetchQueues = async () => {
    try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/queues/${agencyId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
            queues.value = await res.json();
        }
    } catch (err) {
        console.error("Erreur queues:", err);
    }
};

// ✅ Récupérer les langues disponibles depuis l'API
const fetchAvailableLanguages = async () => {
    try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/enabled-languages/${agencyId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
            const languages = await res.json();
            availableLanguages.value = languages;
            console.log('✅ Langues disponibles récupérées:', languages);
        }
    } catch (err) {
        console.error("Erreur récupération langues:", err);
        // En cas d'erreur, garder la langue par défaut
    }
};

const fetchAgencyConfig = async () => {
    try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency-config/${agencyId}`, {
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });
        if (res.status === 404) {
            currentLanguageConfig.value.containers = [{ type: 'main_canvas', blocks: [] }];
            return;
        }
        const data = await res.json();
        config.value = data;

        // Charger les paramètres de background pour la langue active
        if (currentLanguageConfig.value?.branding?.canvasBackground) {
            const bg = currentLanguageConfig.value.branding.canvasBackground;
            canvasBackgroundType.value = bg.type || 'color';
            if (bg.gradient) {
                gradientColor1.value = bg.gradient.color1;
                gradientColor2.value = bg.gradient.color2;
                gradientDirection.value = bg.gradient.direction;
            }
            if (bg.image) {
                canvasBackgroundImage.value = bg.image.url;
                canvasBackgroundSize.value = bg.image.size;
            }
        }
    } catch (err) {
        toast.error("Erreur config : " + err.message);
    }
};

const saveConfig = async () => {
    const idToast = toast.loading("Publication...");
    try {
        const token = localStorage.getItem("superAdminToken");

        // Sauvegarder les paramètres de background pour la langue active
        const canvasBackground = {
            type: canvasBackgroundType.value
        };

        if (canvasBackgroundType.value === 'gradient') {
            canvasBackground.gradient = {
                color1: gradientColor1.value,
                color2: gradientColor2.value,
                direction: gradientDirection.value
            };
        } else if (canvasBackgroundType.value === 'image') {
            canvasBackground.image = {
                url: canvasBackgroundImage.value,
                size: canvasBackgroundSize.value
            };
        }

        // Mettre à jour la config de la langue active avec le background actuel
        currentLanguageConfig.value.branding.canvasBackground = canvasBackground;

        const payload = {
            ...config.value,
            targetResolution: { width: 1080, height: 1920 }
        };

        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency-config/${agencyId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error("Erreur sauvegarde");
        toast.update(idToast, { render: "🚀 Design publié !", type: "success", isLoading: false, autoClose: 3000 });
    } catch (err) {
        toast.update(idToast, { render: err.message, type: "error", isLoading: false, autoClose: 3000 });
    }
};

// === EXPORT/IMPORT ===
const exportConfig = () => {
  // Sauvegarder les paramètres de background pour la langue active avant l'export
  const canvasBackground = {
    type: canvasBackgroundType.value
  };

  if (canvasBackgroundType.value === 'gradient') {
    canvasBackground.gradient = {
      color1: gradientColor1.value,
      color2: gradientColor2.value,
      direction: gradientDirection.value
    };
  } else if (canvasBackgroundType.value === 'image') {
    canvasBackground.image = {
      url: canvasBackgroundImage.value,
      size: canvasBackgroundSize.value
    };
  }

  currentLanguageConfig.value.branding.canvasBackground = canvasBackground;

  const dataStr = JSON.stringify(config.value, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `kiosk-design-${Date.now()}.json`;
  link.click();
  URL.revokeObjectURL(url);
  toast.success("Design exporté!");
};

const importConfig = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const imported = JSON.parse(e.target.result);

      // Nettoyer les IDs MongoDB pour éviter les conflits
      delete imported._id; // Supprimer l'ID de la config
      delete imported.__v; // Supprimer la version MongoDB
      delete imported.createdAt; // Supprimer les timestamps
      delete imported.updatedAt;

      // Remplacer par l'agencyId actuelle
      imported.agencyId = config.value.agencyId;

      // Nettoyer les IDs dans chaque langue
      if (imported.languages) {
        Object.keys(imported.languages).forEach(lang => {
          const langConfig = imported.languages[lang];

          // Nettoyer les éléments canvas : supprimer les _id, régénérer les IDs, vider les queueIds
          if (langConfig.canvasElements && Array.isArray(langConfig.canvasElements)) {
            langConfig.canvasElements = langConfig.canvasElements.map((element) => {
              // Supprimer les IDs MongoDB
              delete element._id;

              // Générer un nouvel ID unique pour l'élément
              element.id = 'element-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

              // Vider les queueIds car ils sont spécifiques à chaque agence
              if (element.data && element.data.queueIds) {
                element.data.queueIds = [];
              }

              return element;
            });
          }
        });
      }

      // Charger la configuration importée
      config.value = imported;

      // Restaurer les paramètres de background pour la langue active
      const activeConfig = config.value.languages[activeLanguage.value];
      if (activeConfig && activeConfig.branding && activeConfig.branding.canvasBackground) {
        const bg = activeConfig.branding.canvasBackground;
        canvasBackgroundType.value = bg.type || 'color';

        if (bg.type === 'gradient' && bg.gradient) {
          gradientColor1.value = bg.gradient.color1;
          gradientColor2.value = bg.gradient.color2;
          gradientDirection.value = bg.gradient.direction;
        } else if (bg.type === 'image' && bg.image) {
          canvasBackgroundImage.value = bg.image.url;
          canvasBackgroundSize.value = bg.image.size || 'cover';
        }
      }

      toast.success("Design importé!");

      // Réinitialiser l'input file pour permettre de réimporter le même fichier
      event.target.value = '';
    } catch (err) {
      toast.error("Fichier invalide");
      event.target.value = '';
    }
  };
  reader.readAsText(file);
};

onMounted(() => {
    fetchAvailableLanguages(); // ✅ Charger les langues disponibles en premier
    fetchAgencyConfig();
    fetchQueues();
});
</script>

<style scoped>
.font-sans { font-family: 'Plus Jakarta Sans', sans-serif; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.scale-up { animation: scaleUp 0.3s ease-out; }
@keyframes scaleUp {
    from { transform: scale(0.9); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
}
</style>